/**
 * Convex Service - Interface to Convex cloud backend
 * Supports HTTP API for both local and cloud deployments
 */

// Convex types matching backend schema
export interface Compartment {
  _id: string;
  _creationTime: number;
  name: string;
  description?: string;
  parentId?: string;
  status: string;
  limits: {
    maxOperators: number;
    maxPatterns: number;
    maxRequestsPerMinute: number;
  };
  metadata?: unknown;
}

export interface Operator {
  _id: string;
  _creationTime: number;
  name: string;
  description?: string;
  compartmentId: string;
  endpoint: string;
  method: string;
  authType: string;
  authConfig?: unknown;
  timeout: number;
  status: string;
  successRate: number;
  avgLatency: number;
  lastInvokedAt?: number;
  version: string;
}

export interface Pattern {
  _id: string;
  _creationTime: number;
  name: string;
  description?: string;
  compartmentId: string;
  triggerType: string;
  triggerValue: string;
  triggerHash: string;
  steps: Array<{
    order: number;
    operatorId: string;
    inputMapping: unknown;
    outputKey: string;
    condition?: string;
    onError: string;
  }>;
  confidence: number;
  executionCount: number;
  successCount: number;
  decay: number;
  lastExecutedAt?: number;
  evolution?: {
    generation: number;
    parentId?: string;
    mutations: string[];
  };
  tags?: string[];
}

export interface DiscoveredPattern {
  _id: string;
  _creationTime: number;
  compartmentId: string;
  source: string;
  intent: string;
  triggerPhrases: string[];
  suggestedOperators: string[];
  confidence: number;
  status: string;
  sourceUrl?: string;
  relatedPatterns?: string[];
  discoveredAt: number;
  promotedAt?: number;
  promotedPatternId?: string;
}

export interface DiscoveredPatternStats {
  total: number;
  byStatus: Record<string, number>;
  bySource: Record<string, number>;
  avgConfidence: number;
  highConfidence: number;
}

export interface SystemStats {
  compartments: { total: number; active: number };
  operators: { total: number; active: number; avgSuccessRate: number };
  patterns: { total: number; highConfidence: number; avgConfidence: number; bypassReady: number };
  scenarios: { total: number; pending: number; completed: number; failed: number };
  executions: { total: number; last24h: number; successRate: number };
}

class ConvexService {
  private url: string = "";
  private client: any = null;

  /**
   * Initialize Convex client
   */
  initialize(convexUrl: string): void {
    this.url = convexUrl;
    if (convexUrl) {
      // Create a simple HTTP client for Convex API
      this.client = {
        query: async (functionPath: string, args: Record<string, unknown> = {}) => {
          const urlWithProtocol = this.url.includes("http") ? this.url : `http://${this.url}`;
          const response = await fetch(`${urlWithProtocol}/api/query`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ path: functionPath, args, format: "json" }),
          });

          if (!response.ok) {
            const text = await response.text();
            throw new Error(`Convex query failed: ${text}`);
          }

          const data = await response.json();
          if (data.status === "error") {
            throw new Error(data.errorMessage || "Query failed");
          }
          return data.value;
        },
        mutation: async (functionPath: string, args: Record<string, unknown> = {}) => {
          const urlWithProtocol = this.url.includes("http") ? this.url : `http://${this.url}`;
          const response = await fetch(`${urlWithProtocol}/api/mutation`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ path: functionPath, args, format: "json" }),
          });

          if (!response.ok) {
            const text = await response.text();
            throw new Error(`Convex mutation failed: ${text}`);
          }

          const data = await response.json();
          if (data.status === "error") {
            throw new Error(data.errorMessage || "Mutation failed");
          }
          return data.value;
        },
      };
    } else {
      this.client = null;
    }
  }

  /**
   * Query any function from Convex
   */
  async query(_functionName: string, _args: any = {}): Promise<any> {
    if (!this.client) {
 throw new Error("Convex not configured");
    }

    try {
      return await (this.client as any).query(_functionName, _args);
    } catch (error) {
      throw new Error(`Query failed: ${error}`);
    }
  }

  /**
   * Call any mutation on Convex
   */
  async mutate(_functionName: string, _args: any = {}): Promise<any> {
    if (!this.client) {
      throw new Error("Convex not configured");
    }

    try {
      return await (this.client as any).mutation(_functionName, _args);
    } catch (error) {
      throw new Error(`Mutation failed: ${error}`);
    }
  }

  /**
   * Check health
   */
  async healthCheck(): Promise<boolean> {
    if (!this.client) {
      return false;
    }

    try {
      // Call a simple query
      await this.query("system:health", {});
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Check if configured
   */
  isConfigured(): boolean {
    return !!this.client && !!this.url;
  }

  // =========== Operator Functions ===========

  async listOperators(opts?: { compartmentId?: string; limit?: number }): Promise<Operator[]> {
    try {
      return await this.query("operators:list", opts || {});
    } catch {
      return [];
    }
  }

  async getOperator(id: string): Promise<Operator | null> {
    try {
      return await this.query("operators:get", { id });
    } catch {
      return null;
    }
  }

  async createOperator(data: {
    name: string;
    description?: string;
    endpoint: string;
    method: string;
    authType: string;
    timeout: number;
  }): Promise<string> {
    return await this.mutate("operators:create", data);
  }

  async updateOperator(
    id: string,
    data: Partial<Operator>
  ): Promise<void> {
    return await this.mutate("operators:update", { id, ...data });
  }

  async deleteOperator(id: string): Promise<void> {
    return await this.mutate("operators:remove", { id });
  }

  // =========== Pattern Functions ===========

  async listPatterns(opts?: { compartmentId?: string; limit?: number }): Promise<Pattern[]> {
    try {
      return await this.query("patterns:list", opts || {});
    } catch {
      return [];
    }
  }

  async getPattern(id: string): Promise<Pattern | null> {
    try {
      return await this.query("patterns:get", { id });
    } catch {
      return null;
    }
  }

  async createPattern(data: {
    name: string;
    triggerValue: string;
    triggerHash: string;
    steps: any[];
    confidence?: number;
    tags?: string[];
  }): Promise<string> {
    return await this.mutate("patterns:create", data);
  }

  async updatePattern(
    id: string,
    data: Partial<Pattern>
  ): Promise<void> {
    return await this.mutate("patterns:update", { id, ...data });
  }

  async deletePattern(id: string): Promise<void> {
    return await this.mutate("patterns:remove", { id });
  }

  async executePattern(id: string, input: any): Promise<any> {
    return await this.mutate("patterns:execute", { id, input });
  }

  async getPatternStats(): Promise<DiscoveredPatternStats> {
    try {
      return await this.query("patterns:stats", {});
    } catch {
      return {
        total: 0,
        byStatus: {},
        bySource: {},
        avgConfidence: 0,
        highConfidence: 0,
      };
    }
  }

  // =========== Discovery Functions ===========

  async getDiscoveredPatterns(opts?: {
    limit?: number;
    minConfidence?: number;
  }): Promise<DiscoveredPattern[]> {
    try {
      return await this.query("discovery:list", opts || {});
    } catch {
      return [];
    }
  }

  async promotePattern(id: string, operatorIds: string[]): Promise<string> {
    return await this.mutate("discovery:promote", { id, operatorIds });
  }

  async rejectPattern(id: string): Promise<void> {
    return await this.mutate("discovery:reject", { id });
  }

  // =========== Auth Functions ===========

  async validateSession(sessionId: string): Promise<{ valid: boolean; level?: string }> {
    try {
      return await this.mutate("creatorAuth:validateSession", {
        sessionId,
      });
    } catch {
      return { valid: false };
    }
  }

  async createSession(level: string, expiresAt: number): Promise<string> {
    return await this.mutate("creatorAuth:createSession", {
      level,
      expiresAt,
    });
  }

  async updateCounter(sessionId: string): Promise<void> {
    return await this.mutate("creatorAuth:updateCounter", { sessionId });
  }

  async endSession(sessionId: string): Promise<void> {
    return await this.mutate("creatorAuth:endSession", { sessionId });
  }

  // =========== System Stats ===========

  async getSystemStats(): Promise<SystemStats> {
    try {
      return await this.query("system:stats", {});
    } catch {
      return {
        compartments: { total: 0, active: 0 },
        operators: { total: 0, active: 0, avgSuccessRate: 0 },
        patterns: {
          total: 0,
          highConfidence: 0,
          avgConfidence: 0,
          bypassReady: 0,
        },
        scenarios: {
          total: 0,
          pending: 0,
          completed: 0,
          failed: 0,
        },
        executions: { total: 0, last24h: 0, successRate: 0 },
      };
    }
  }
}

// Export singleton instance
export const convexService = new ConvexService();
